import { Crown, Check, Zap, BarChart3, Building, Shield, ShieldCheck, Users, DollarSign, CreditCard, AlertTriangle, TrendingUp, Clock, Calendar, ChevronDown, ChevronUp, Feather, X } from "lucide-react";
import { humanizeStatus } from "@/app/_module/utils/humanizeStatus";
import { allowCheckout } from "@/app/_module/utils/nativeCheckoutGuard";
import { isNativePlatform } from "@/lib/capacitor/platform";
import InsuraBotIcon from "@/app/(authenticated)/ai-calling/_modules/components/InsuraBotIcon";
import { useRouter } from "next/navigation";
import { useState, useEffect, useRef } from "react";
import { useQueryClient, useQuery } from "@tanstack/react-query";

import { Badge } from "@/app/_module/components/ui/badge";
import { Button } from "@/app/_module/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/app/_module/components/ui/card";
import { Switch } from "@/app/_module/components/ui/switch";
import { Label } from "@/app/_module/components/ui/label";
import { Progress } from "@/app/_module/components/ui/progress";
import {
  Dialog,
  ResponsiveDialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/app/_module/components/ui/dialog";
import { shouldUseDryRun } from "@/app/_module/utils/environment";
import { useUserContext } from "@/app/_module/store/contexts/userContext";
import { accountTypeHasTelephony } from "@/app/_module/components/TelephonyEnabled";
import { useToast } from "@/app/_module/hooks/use-toast";
import { getSupabaseSession } from "@/app/_module/services/auth";
import { useTrialStatus } from "@/app/_module/hooks/useTrialStatus";

import {
  useGetSubscriptionPlans,
  useGetMyActiveSubscription,
  useSyncSubscription,
} from "../hooks/queries";
import {
  createCheckoutSession,
  completeDryRunSubscription,
  getUsageStats,
} from "../services/subscription-plan-services";
import type { SubscriptionPlan } from "../types/subscription-plans";

import config from "@/app/_module/services/config";
import { getApiUrl } from "@/app/_module/services/config";
import { VerifyPurchasesResult, type VerifyPurchasesData } from "./VerifyPurchasesResult";
import { endpoint } from "@/app/_module/services/endpoints";
import { AgencyBillingDashboard } from "./AgencyBillingDashboard";
import { useBranding } from "@/app/_module/store/contexts/brandingContext";
import { getEnterpriseCoverageStatus, type EnterpriseCoverageStatus } from "@/app/_module/services/api/agency-billing.api";
import { isDemoMode } from "@/app/_module/utils/demoMode";

/**
 * Plans that are announced but not purchasable yet: they render with a "Coming Soon"
 * badge and a disabled button so nobody can check out into a tier that has no Stripe
 * price behind it.
 *
 * Super Agent is the only one left. (Lite was here until 2026-09-03, when it got its
 * `subscription_plans` + `account_types` rows and a real Stripe price; before that its
 * live button let a Full-Fledged customer "upgrade" into a plan billed at nothing.)
 *
 * Matched on NAME rather than plan id because these arrive from three different sources
 * (subscription_plans rows, account_types rows, and the config merge) and only the name
 * is reliably present on all three.
 *
 * TO LAUNCH one of these: give it a subscription_plans row with a real stripe_price_id,
 * then remove it from this list.
 */
/**
 * Plan tiers, low to high. Used to word the card buttons: moving DOWN the list
 * is a downgrade, not an "upgrade to Lite". Matched on the plan NAME because
 * the cards arrive from three sources (subscription_plans rows, account_types
 * rows, and the config merge) and only the name is on all three.
 */
const PLAN_RANK: Array<{ test: (n: string) => boolean; rank: number }> = [
  { test: (n) => n.includes("lite"), rank: 1 },
  { test: (n) => n.includes("basic") || n.includes("starter"), rank: 2 },
  { test: (n) => n.includes("full") || n.includes("fledged") || n.includes("premium"), rank: 3 },
  { test: (n) => n.includes("super"), rank: 4 },
  { test: (n) => n.includes("enterprise"), rank: 5 },
];

const planRank = (planName?: string | null): number => {
  const n = String(planName ?? "").toLowerCase().replace(/_/g, " ");
  if (!n) return 0; // unknown / no plan — never reads as a downgrade
  return PLAN_RANK.find((p) => p.test(n))?.rank ?? 0;
};

const isComingSoonPlan = (planName: string): boolean => {
  const n = (planName || "").toLowerCase();
  // Lite launched 2026-09-03 (subscription_plans row + Stripe price). Super Agent stays.
  return n.includes("super");
};

/**
 * Feature lines that name what a plan does NOT include ("No dialer or outbound
 * calling") render with a gray ✕ and muted text instead of a green check — a green
 * check next to "No dialer" reads as if the absence were a benefit.
 *
 * Convention: any feature string starting with "No " (case-insensitive) is an
 * exclusion. Feature lists arrive from both account_types DB rows and the backend
 * ACCOUNT_TYPE_CONFIG merge, so a text prefix is the only marker both sources can
 * express. Lite (CRM with telephony removed) is the only plan using it today.
 */
const isExclusionFeature = (label: string): boolean => /^no\s/i.test(label || "");

/**
 * Per-tier visual identity for the pricing grid. Deliberately ONE restrained accent per
 * tier on a white card — the premium read comes from hierarchy, spacing and a single
 * 1px gradient keyline per card, not from saturating whole cards. Enterprise is the one
 * intentional contrast moment: a dark ink panel that anchors the page.
 */
const getPlanTheme = (planName: string) => {
  const n = (planName || "").toLowerCase();
  if (n.includes("super")) {
    return {
      bar: "from-amber-400 via-amber-300 to-yellow-200",
      iconWrap: "bg-amber-50 text-amber-600 ring-1 ring-amber-200",
      price: "text-amber-600",
      check: "text-amber-400",
      tagline: "AI-powered outbound calling + more",
    };
  }
  if (n.includes("full") || n.includes("fledged") || n.includes("premium")) {
    return {
      bar: "from-indigo-600 via-blue-500 to-sky-400",
      iconWrap: "bg-indigo-50 text-indigo-600 ring-1 ring-indigo-100",
      price: "text-indigo-600",
      check: "text-indigo-500",
      tagline: "Unlimited dialing, AI & automation",
    };
  }
  if (n.includes("basic") || n.includes("starter")) {
    return {
      bar: "from-slate-700 via-slate-500 to-slate-300",
      iconWrap: "bg-slate-100 text-slate-600 ring-1 ring-slate-200",
      price: "text-slate-900",
      check: "text-emerald-500",
      tagline: "Everything you need to work your book",
    };
  }
  if (n.includes("lite")) {
    return {
      bar: "from-stone-400 via-stone-300 to-stone-200",
      iconWrap: "bg-stone-100 text-stone-500 ring-1 ring-stone-200",
      price: "text-slate-900",
      check: "text-emerald-500",
      tagline: "The full CRM, without the dialers and calling",
    };
  }
  return {
    bar: "from-violet-600 via-purple-500 to-fuchsia-400",
    iconWrap: "bg-violet-50 text-violet-600 ring-1 ring-violet-100",
    price: "text-violet-600",
    check: "text-violet-500",
    tagline: "For agencies at scale",
  };
};


// Configuration
const ENTERPRISE_CALENDLY_URL = 'https://calendly.com/insuracentral/insuracentral-demo';

// Feature label mapping for human-readable display
const FEATURE_LABELS: Record<string, string> = {
  // Basic features
  'power_dialer': 'Power dialer',
  'policy_upload_management': 'Policy upload & management',
  'lead_management': 'Lead management: policies, documents, personal data, notes',
  'unlimited_calls_history': 'Unlimited calls & history',
  'unlimited_sms_history': 'Unlimited SMS & history',
  'appointment_scheduling_reminders': 'Appointment scheduling & reminders',
  'weekly_monthly_pdf_reports': 'Weekly/monthly PDF performance reports',

  // Full-Fledged features
  'automated_greetings': 'Automated greetings for birthdays & holidays',
  'call_forwarding': 'Forward incoming calls to your phone',
  'ai_bot_access': 'AI underwriting bot access',
  'call_recordings': 'Unlimited call recordings',
  'Agency creation and management': 'Agency creation and management',

  // Super Agent features (AI Calling)
  'everything_in_full_fledged': 'Everything in Full-Fledged, plus:',
  'ai_outbound_calling': 'AI-powered outbound calling agent',
  'ai_calling_campaigns': 'AI calling campaigns with smart scheduling',
  'ai_live_transfer': 'AI live transfer to agents',
  'ai_call_monitoring': 'Real-time AI call monitoring dashboard',
  'ai_disposition_automation': 'Automated call disposition & follow-up',
  'ai_lead_pooling': 'AI lead pooling & smart area code matching',
  'ai_voice_cloning': 'AI voice cloning — clone your voice from a 30s recording',
  'ringless_voicemail': 'Ringless voicemail drops (no phone ring)',
  'auto_sentimental_voicemail': 'Auto birthday & holiday voicemails with cloned voice',

  // Enterprise features
  'unlimited_everything': 'Everything in Fully-Fledged, plus:',
  'custom_integrations': 'Custom integrations with lead vendors, CRMs, or internal tools',
  'dedicated_account_manager': 'Dedicated account manager for onboarding & success',
  'white_label_options': 'White-labeled PDFs and reporting',
  'custom_api_access': 'Custom AI model training for your agency',
  'enterprise_security': 'Multi-user & team management (advanced permissions, team dashboards)',
  'sla_guarantees': 'Priority support with SLA (faster response times, phone & chat support)',
  'priority_support_24_7': 'Priority 24/7 support',
  'custom_onboarding': 'Custom onboarding & training',
};

// Helper function to format feature text
const formatFeature = (feature: string): string => {
  // If it's already a readable sentence (contains spaces or special chars), return as-is
  if (feature.includes(' ') || feature.includes(':') || feature.includes('(')) {
    return feature;
  }
  // Otherwise, look up in mapping or return with underscores replaced
  return FEATURE_LABELS[feature] || feature.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

interface Plan {
  name: string;
  price: number;
  period: "month" | "year";
  features: string[];
  isPopular?: boolean;
  current?: boolean;
  id: string;
}

interface SubscriptionPlansProps {
  isAgencyOwner?: boolean;
  isEnterpriseOwner?: boolean;
}

// Clock SVG Icon
const ClockIcon = ({ className }: { className?: string }) => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    width="24" 
    height="24" 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="2" 
    strokeLinecap="round" 
    strokeLinejoin="round" 
    className={className}
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

// Warning Triangle SVG Icon
const WarningIcon = ({ className }: { className?: string }) => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    width="24" 
    height="24" 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="2" 
    strokeLinecap="round" 
    strokeLinejoin="round" 
    className={className}
    aria-hidden="true"
  >
    <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3" />
    <path d="M12 9v4" />
    <path d="M12 17h.01" />
  </svg>
);

// Trial Countdown Component - Shows real-time countdown of trial remaining
const TrialCountdown = ({ 
  trialEndDate, 
  daysRemaining,
  trialType,
  hasPaidSubscription
}: { 
  trialEndDate: string | Date | null; 
  daysRemaining: number;
  trialType?: string | null;
  hasPaidSubscription?: boolean;
}) => {
  const [timeLeft, setTimeLeft] = useState<{
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
    total: number;
  }>({ days: 0, hours: 0, minutes: 0, seconds: 0, total: 0 });

  useEffect(() => {
    if (!trialEndDate) return;

    const calculateTimeLeft = () => {
      const now = new Date().getTime();
      const endTime = new Date(trialEndDate).getTime();
      const difference = endTime - now;

      if (difference <= 0) {
        return { days: 0, hours: 0, minutes: 0, seconds: 0, total: 0 };
      }

      return {
        days: Math.floor(difference / (1000 * 60 * 60 * 24)),
        hours: Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
        minutes: Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60)),
        seconds: Math.floor((difference % (1000 * 60)) / 1000),
        total: difference,
      };
    };

    // Initial calculation
    setTimeLeft(calculateTimeLeft());

    // Update every second
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);

    return () => clearInterval(timer);
  }, [trialEndDate]);

  // Determine urgency level for styling
  const isUrgent = timeLeft.total > 0 && timeLeft.total <= 60 * 60 * 1000; // Less than 1 hour
  const isWarning = timeLeft.total > 0 && timeLeft.days === 0 && timeLeft.hours <= 24; // Less than 24 hours
  const isExpired = timeLeft.total <= 0;

  // Format the countdown string based on time remaining
  const formatCountdown = () => {
    if (timeLeft.days > 0) {
      return `${timeLeft.days} day${timeLeft.days !== 1 ? 's' : ''}, ${timeLeft.hours}h ${timeLeft.minutes}m`;
    }
    if (timeLeft.hours > 0) {
      return `${timeLeft.hours}h ${timeLeft.minutes}m ${timeLeft.seconds}s`;
    }
    return `${timeLeft.minutes}m ${timeLeft.seconds}s`;
  };

  // Format expiry date
  const formatExpiryDate = () => {
    if (!trialEndDate) return '';
    return new Date(trialEndDate).toLocaleDateString('en-US', {
      month: 'numeric',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Only show expired message if trial is expired AND user doesn't have a paid subscription
  if (isExpired && !hasPaidSubscription) {
    // Format the expiry date
    const expiredDate = trialEndDate 
      ? new Date(trialEndDate).toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' })
      : 'recently';
    
    return (
      <span className="block bg-red-50 border-l-4 border-red-400 p-4 rounded-r">
        <span className="flex items-center">
          <WarningIcon className="h-5 w-5 text-red-400 mr-3 flex-shrink-0" />
          <span>
            <span className="block text-sm font-medium text-red-800">Trial Expired - Account in Full Lockdown Mode</span>
            <span className="block text-sm text-red-700 mt-1">
              Your trial ended on {expiredDate}. All features are locked. Click to upgrade and restore access.
            </span>
          </span>
        </span>
      </span>
    );
  }
  
  // If user has paid subscription, don't show trial countdown at all
  if (hasPaidSubscription) {
    return null;
  }

  // Determine colors based on urgency
  const bgColor = isUrgent ? 'bg-red-50' : isWarning ? 'bg-yellow-50' : 'bg-blue-50';
  const borderColor = isUrgent ? 'border-red-400' : isWarning ? 'border-yellow-400' : 'border-blue-400';
  const iconColor = isUrgent ? 'text-red-400' : isWarning ? 'text-yellow-400' : 'text-blue-400';
  const titleColor = isUrgent ? 'text-red-800' : isWarning ? 'text-yellow-800' : 'text-blue-800';
  const textColor = isUrgent ? 'text-red-700' : isWarning ? 'text-yellow-700' : 'text-blue-700';

  return (
    <span className={`block ${bgColor} border-l-4 ${borderColor} p-4 rounded-r`}>
      <span className="flex items-start">
        {isUrgent || isWarning ? (
          <WarningIcon className={`h-5 w-5 ${iconColor} mr-3 flex-shrink-0 mt-0.5`} />
        ) : (
          <ClockIcon className={`h-5 w-5 ${iconColor} mr-3 flex-shrink-0 mt-0.5`} />
        )}
        <span className="flex-1">
          <span className={`block text-sm font-medium ${titleColor}`}>
            {isUrgent ? 'Trial Ending Very Soon!' : isWarning ? 'Trial Ending Soon' : 'Trial Active'}
          </span>
          <span className={`block text-sm ${textColor} mt-1 space-y-1`}>
            <span className="block">
              • Your trial ends in{' '}
              <span className={`font-semibold ${isUrgent ? 'animate-pulse' : ''}`}>
                {formatCountdown()}
              </span>
              {' '}on {formatExpiryDate()}.
            </span>
            {(isUrgent || isWarning) && (
              <span className="block font-medium">
                Upgrade now to keep your trial features active!
              </span>
            )}
          </span>
        </span>
      </span>
    </span>
  );
};

export const SubscriptionPlans = function ({ isAgencyOwner = false, isEnterpriseOwner: isEnterpriseOwnerProp = false }: SubscriptionPlansProps) {
  // Store compliance: EVERY surface that shows plan cards (settings tab,
  // /m/settings/subscription section screen, agency billing tab) pops the
  // "available on the web version" explainer inside the native app. Lives on
  // the component, not the routes, so a new render site can't miss it. No-op
  // on web.
  useEffect(() => {
    allowCheckout("subscription");
  }, []);

  // …and on native the whole component renders an instruction-only card
  // instead of plan cards (see the early return below the hooks).
  const [isNativeApp, setIsNativeApp] = useState(false);
  useEffect(() => {
    setIsNativeApp(isNativePlatform());
  }, []);

  const [verifyResult, setVerifyResult] = useState<VerifyPurchasesData | null>(null);
  const [verifyError, setVerifyError] = useState<string | null>(null);
  const [verifyOpen, setVerifyOpen] = useState(false);
  const { branding } = useBranding();
  const [billingPeriod, setBillingPeriod] = useState<"monthly" | "yearly">(
    "monthly"
  );
  const [loadingPlan, setLoadingPlan] = useState<string | null>(null);
  const [showCalendlyPopup, setShowCalendlyPopup] = useState(false);
  const [agencyBillingSettings, setAgencyBillingSettings] = useState({
    seatPayor: 'agent' as 'agency' | 'agent',
    plan: 'basic' as 'lite' | 'basic' | 'full_fledged',
    billingInterval: 'monthly' as 'monthly' | 'yearly',
  });
  const [isLoadingAgencySettings, setIsLoadingAgencySettings] = useState(false);
  const [showAgencyOwnerModal, setShowAgencyOwnerModal] = useState(false);
  // Downgrading to Lite deactivates every number: say so before Stripe, not after.
  const [liteDowngradePlan, setLiteDowngradePlan] = useState<(Plan & { id: string }) | null>(null);
  const router = useRouter();
  const { user, refetchUser } = useUserContext();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const syncSubscription = useSyncSubscription();

  // Get trial status from the users table
  const { isTrialActive, daysRemaining: trialDaysRemaining, trialEndDate, trialType, isExpired, hasPaidSubscription } = useTrialStatus();

  // Fetch enterprise status directly so we don't rely solely on the parent prop
  const { data: enterpriseStatus, isLoading: isLoadingEnterprise } = useQuery<EnterpriseCoverageStatus>({
    queryKey: ['enterprise-coverage-status'],
    queryFn: getEnterpriseCoverageStatus,
    staleTime: 1000 * 60 * 5,
  });

  // Enterprise owner detection: API response, prop from parent, or accountType
  const isEnterpriseAccountType = user?.user?.accountType === 'enterprise';
  const isEnterpriseOwner = enterpriseStatus?.isOwner === true || isEnterpriseOwnerProp || isEnterpriseAccountType;

  // Usage Stats Hook - fetch usage data (must be before any early returns)
  const { data: usageStats, isLoading: isLoadingUsageStats } = useQuery({
    queryKey: ['subscription', 'usage-stats'],
    queryFn: getUsageStats,
    refetchInterval: 120000, // Refresh every 2 minutes for usage stats
  });

  // State for expanding/collapsing usage stats
  const [showUsageStats, setShowUsageStats] = useState(false);

  // State for showing/hiding Enterprise plan
  // Expanded by default — Enterprise hidden behind a chevron meant most owners never saw
  // the tier existed. The toggle stays so it can still be collapsed.
  const [showEnterprise, setShowEnterprise] = useState(true);

  // Track processed session IDs to prevent double-execution
  const processedSessionsRef = useRef<Set<string>>(new Set());

  // Extract stable primitive value for useEffect dependency
  const currentAgencyId = user?.currentAgency?.agency?.id;

  // Load agency billing settings from backend
  useEffect(() => {
    const loadAgencyBillingSettings = async () => {
      if (!isAgencyOwner || !currentAgencyId) return;

      try {
        // Get Supabase JWT token for authentication
        const { getSupabaseClient } = await import('@/app/_module/services/supabaseClient');
        const supabase = await getSupabaseClient();
        const { data: { session } } = await supabase.auth.getSession();

        if (!session?.access_token) {
          return;
        }

        const response = await fetch(getApiUrl(endpoint.agency.getBillingSettings(currentAgencyId)), {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
        });

        if (response.ok) {
          const settings = await response.json();
          setAgencyBillingSettings({
            seatPayor: settings.seatPayor || 'agent',
            plan: settings.plan || 'basic',
            billingInterval: settings.billingInterval || 'monthly',
          });
        } else if (response.status === 404) {
          // Agency billing settings endpoint doesn't exist yet - create default settings
          const defaultSettings = {
            seatPayor: 'agent' as const,
            plan: 'basic' as const, 
            billingInterval: 'monthly' as const,
          };
          setAgencyBillingSettings(defaultSettings);
          
          // Try to create default settings on the backend
          try {
            await fetch(getApiUrl(endpoint.agency.updateBillingSettings(currentAgencyId)), {
              method: 'PUT',
              headers: {
                'Authorization': `Bearer ${session.access_token}`,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify(defaultSettings),
            });
          } catch (createError) {
          }
        }
      } catch (error) {

      }
    };

    loadAgencyBillingSettings();
  }, [isAgencyOwner, currentAgencyId]);

  // Check if returning from billing portal and sync subscription
  useEffect(() => {
    // Check sessionStorage for billing portal flag
    const billingPortalOpened = sessionStorage.getItem('billingPortalOpened');

    if (billingPortalOpened === 'true') {
      // Clear the flag immediately to avoid repeated syncs
      sessionStorage.removeItem('billingPortalOpened');

      // Trigger sync
      syncSubscription.mutate(undefined, {
        onSuccess: () => {
        },
        onError: (error) => {
        },
      });
    }
  }, [syncSubscription]);

  // Fetch subscription plans from the API - Using context=settings to get all plans including Enterprise
  const {
    data: subscriptionPlansData,
    isLoading,
    error,
  } = useGetSubscriptionPlans({
    page: 1,
    limit: 10,
  });

  // Debug logging (development only)
  if (process.env.NODE_ENV === 'production') {
  }

  // Fetch user's active subscription
  const { data: activeSubscriptionData, error: activeError } = useGetMyActiveSubscription();

  // Handle successful subscription purchase redirect from Stripe
  // This effect runs on mount AND whenever URL changes
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const status = urlParams.get('status');
    const sessionId = urlParams.get('session_id');

    if (status === 'success' && sessionId) {
      // Check if we've already processed this session
      if (processedSessionsRef.current.has(sessionId)) {
        return;
      }

      // Mark this session as being processed
      processedSessionsRef.current.add(sessionId);

      // Call fulfillment API to activate subscription
      const fulfillCheckout = async () => {
        try {
          const { getSupabaseClient } = await import('@/app/_module/services/supabaseClient');
          const supabase = await getSupabaseClient();
          const { data: { session } } = await supabase.auth.getSession();

          if (!session?.access_token) {
            throw new Error('No authentication session');
          }

          const response = await fetch(getApiUrl(`/subscriptions/checkout/fulfill/${sessionId}`), {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${session.access_token}`,
              'Content-Type': 'application/json',
            },
          });

          if (response.ok) {
            const result = await response.json();

            // Ad-pixel "Purchase" conversion — the REAL revenue event (a paid
            // subscription, not the free-trial signup). Fully fire-and-forget:
            // wrapped so a pixel/SDK error can never break activation. event_id
            // = Stripe checkout session id so a future server-side Events API
            // call dedupes against this client event.
            try {
              const value = Number(result?.data?.amount) || 0;
              const currency = result?.data?.currency || "USD";
              const sub = result?.data?.subscription;
              const planName =
                sub?.planName || sub?.plan?.name || sub?.accountType || "subscription";
              const ttq = (window as any).ttq;
              if (ttq && typeof ttq.track === "function") {
                ttq.track(
                  "Purchase",
                  { value, currency, content_type: "subscription", content_id: planName, content_name: planName },
                  { event_id: sessionId },
                );
              }
              const fbq = (window as any).fbq;
              if (typeof fbq === "function") {
                fbq("track", "Purchase", { value, currency, content_name: planName }, { eventID: sessionId });
              }
            } catch {
              // never block subscription activation on a pixel failure
            }

            toast({
              title: "Subscription Activated!",
              description: "Your subscription has been successfully activated. Welcome aboard!",
              duration: 6000,
            });
          } else {
            const errorText = await response.text();
            // Even if fulfillment fails, show success (payment was successful)
            toast({
              title: "Payment Successful",
              description: "Your payment was received. Your subscription will be activated shortly.",
              duration: 6000,
            });
          }

          // Refresh ALL subscription and transaction related data
          queryClient.invalidateQueries({ queryKey: ["subscription-plans"] });
          queryClient.invalidateQueries({ queryKey: ["active-subscription"] });
          queryClient.invalidateQueries({ queryKey: ["my-active-subscription"] });
          queryClient.invalidateQueries({ queryKey: ["purchaseHistory"] });
          queryClient.invalidateQueries({ queryKey: ["user"] });
          
          // Also sync subscription from Stripe to ensure backend is up to date
          syncSubscription.mutate();
          
          // CRITICAL: Refresh the page after a short delay to ensure backend has processed the subscription
          // This is similar to how trial expiration triggers a page refresh
          // The refresh will reload user context and update all UI components with the new subscription status
          setTimeout(() => {
            window.location.reload();
          }, 2000); // 2 second delay to allow backend to process subscription

        } catch (error) {
          // Even on error, show success (payment was successful)
          toast({
            title: "Payment Successful",
            description: "Your payment was received. Your subscription will be activated shortly.",
            duration: 6000,
          });

          // Still invalidate queries
          queryClient.invalidateQueries({ queryKey: ["subscription-plans"] });
          queryClient.invalidateQueries({ queryKey: ["active-subscription"] });
          queryClient.invalidateQueries({ queryKey: ["my-active-subscription"] });
          queryClient.invalidateQueries({ queryKey: ["purchaseHistory"] });
          queryClient.invalidateQueries({ queryKey: ["user"] });
          
          // Sync subscription from Stripe
          syncSubscription.mutate();
          
          // Refresh the page even on error (payment was successful)
          setTimeout(() => {
            window.location.reload();
          }, 3000); // Longer delay on error to give backend more time
        }
      };

      fulfillCheckout();

      // Clean up URL
      window.history.replaceState({}, '', window.location.pathname + '?tab=subscription');
    } else if (status === 'cancelled' && sessionId) {
      // Mark transaction as FAILED on backend
      fetch(`${process.env.NEXT_PUBLIC_API_URL}/subscriptions/checkout-sessions/${sessionId}/cancel`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        },
      }).catch((error) => {
      });

      toast({
        title: "Subscription Cancelled",
        description: "Your subscription purchase was cancelled. No charges were made.",
        variant: "destructive",
        duration: 4000,
      });

      // Refresh purchase history to show FAILED status
      queryClient.invalidateQueries({ queryKey: ["purchaseHistory"] });

      // Clean up URL
      window.history.replaceState({}, '', window.location.pathname + '?tab=subscription');
    }
  }, [toast, queryClient]);

  // Transform API data to match your existing Plan interface
  const { regularPlans, agencyPaymentPlan } = (() => {
    // Handle both response formats
    let plansData: SubscriptionPlan[] = [];

    if (Array.isArray(subscriptionPlansData)) {
      // Direct array response from /plans/available
      plansData = subscriptionPlansData;
    } else if (subscriptionPlansData?.items && Array.isArray(subscriptionPlansData.items)) {
      // Paginated response from /subscription-plans
      plansData = subscriptionPlansData.items;
    }

    // Debug logging
    // Ensure plansData is an array before mapping
    if (!Array.isArray(plansData)) {

      return { regularPlans: [], agencyPaymentPlan: null };
    }

    // Separate agency payment plan from regular plans
    let agencyPlan: Plan | null = null;
    const regular: Plan[] = [];

    plansData.forEach((plan: SubscriptionPlan) => {
      const planName = plan.name.toLowerCase();
      const planId = (plan.id || '').toLowerCase();
      // Normalize plan names for comparison (handle case and underscore/hyphen differences)
      const normalizePlanName = (name: string) => {
        return name.toLowerCase().replace(/-/g, '_').replace(/\s+/g, '_');
      };

      const activePlanName = activeSubscriptionData?.plan?.name || "";
      const isCurrentPlan = normalizePlanName(activePlanName) === normalizePlanName(plan.name);

      const transformedPlan: Plan = {
        name: plan.name,
        price: plan.monthlyPrice || parseFloat(String(plan.price)),
        period: (billingPeriod === "monthly" ? "month" : "year") as "month" | "year",
        features: Array.isArray((plan as unknown as { features?: unknown }).features)
          ? (((plan as unknown as { features?: unknown[] }).features as unknown[]).map(String))
          : [],
        isPopular: planName.includes("full_fledged") || planName.includes("full-fledged") || planName.includes("premium") || planName.includes("super_agent") || planName.includes("super agent"),
        current: isCurrentPlan,
        id: plan.id,
      };

      // Check if this is the agency payment plan
      if (planId === 'agency_pays_for_agents' || planName.includes('agency pays for agents')) {
        agencyPlan = transformedPlan;
      } else {
        regular.push(transformedPlan);
      }
    });

    return { regularPlans: regular, agencyPaymentPlan: agencyPlan };
  })();

  const plans = regularPlans;

  // Sort plans in a logical order: Lite, Basic, Full-Fledged, Super Agent, Enterprise
  const sortedPlans = plans.sort((a, b) => {
    const getOrder = (name: string) => {
      const lowerName = name.toLowerCase();
      // Lite is the entry tier — it sits BEFORE Basic so the ladder reads cheapest-first.
      if (lowerName.includes('lite')) return 0;
      if (lowerName.includes('basic') || lowerName.includes('starter')) return 1;
      if (lowerName.includes('full_fledged') || lowerName.includes('full-fledged') || lowerName.includes('premium') || lowerName.includes('professional')) return 2;
      if (lowerName.includes('super') || lowerName.includes('super_agent')) return 3;
      if (lowerName.includes('enterprise') || lowerName.includes('business')) return 4;
      return 5; // Other plans at the end
    };
    return getOrder(a.name) - getOrder(b.name);
  });

  // Debug logging (development only)
  if (process.env.NODE_ENV === 'production') {
  }

  // Update agency billing settings
  const updateAgencyBillingSettings = async (newSettings: typeof agencyBillingSettings) => {
    if (!isAgencyOwner || !user?.currentAgency?.agency?.id) return;
    
    try {
      const session = await getSupabaseSession();
      if (!session?.access_token) return;

      const response = await fetch(getApiUrl(endpoint.agency.updateBillingSettings(user.currentAgency.agency.id)), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(newSettings),
      });

      if (response.ok) {
        setAgencyBillingSettings(newSettings);
        toast({
          title: "Settings Updated",
          description: "Agency billing settings have been updated successfully.",
        });
      } else {
        throw new Error('Failed to update agency billing settings');
      }
    } catch (error) {

      toast({
        title: "Update Failed",
        description: "Failed to update agency billing settings. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleSubscribe = async (plan: Plan & { id: string }, liteDowngradeConfirmed = false) => {
    // DEMO SANDBOX: this button's real job IS the trial offer — route it there.
    //
    // Left alone it redirects to a live Stripe checkout for the pool account, which is
    // both blocked server-side (Stripe proxy) and the wrong pitch: the visitor should
    // be starting THEIR OWN trial, not upgrading a shared demo seat.
    if (isDemoMode()) {
      window.dispatchEvent(
        new CustomEvent('demo-restricted', {
          detail:
            'Subscriptions are managed on real accounts. Start a free trial to pick a plan for your agency.',
        }),
      );
      return;
    }

    setLoadingPlan(plan.id);

    try {
      // Handle Enterprise plan - open Calendly popup for sales consultation
      if (plan.name.toLowerCase().includes('enterprise')) {
        // Open Calendly popup widget instead of redirecting
        setShowCalendlyPopup(true);
        setLoadingPlan(null);
        return;
      }

      // Moving from a plan with a phone to Lite: numbers are deactivated and
      // their billing stops the moment the plan changes. Confirm first.
      const currentType = (user as any)?.user?.accountType ?? (user as any)?.accountType;
      const currentTrialType = (user as any)?.user?.trialAccountTypeUsed ?? (user as any)?.trialAccountTypeUsed;
      if (
        plan.name.toLowerCase().includes('lite') &&
        accountTypeHasTelephony(currentType, currentTrialType) &&
        !liteDowngradeConfirmed
      ) {
        setLiteDowngradePlan(plan);
        setLoadingPlan(null);
        return;
      }

      // Check if user is trying to downgrade to Basic
      // Check both user account type and current subscription plan
      // The account type lives at user.user.accountType; reading it off the
      // wrapper returned undefined, so this guard never fired for someone whose
      // subscription row was missing a plan name.
      const u: any = (user as any)?.user || user || {};
      const currentRank = planRank(activeSubscriptionData?.plan?.name || u.accountType || u.selectedPlan);
      const isCurrentlyFullFledged = currentRank >= planRank('full_fledged');

      const isDowngradeToBasic = plan.name.toLowerCase().includes('basic') && isCurrentlyFullFledged;

      // If agency owner is trying to downgrade to Basic, show restriction dialog
      if (isDowngradeToBasic && isAgencyOwner) {
        setShowAgencyOwnerModal(true);
        setLoadingPlan(null);
        return;
      }

      // Handle Basic and Full-Fledged plans - redirect to Stripe checkout (NOT billing portal)
      // For trial users upgrading, ensure they get the proper billing cycle
      // Database schema supports: account_status = 'trial_active', is_trial_active = true
      const isTrialUpgrade = activeSubscriptionData?.status === 'trialing' ||
                           activeSubscriptionData?.status === 'trial_active';

      // Check if user has an active (non-trial) subscription
      // Accept both 'active' and 'ACTIVE' status
      const hasActiveSubscription = activeSubscriptionData &&
                                   !isTrialUpgrade &&
                                   (activeSubscriptionData.status === 'active' ||
                                    activeSubscriptionData.status === 'ACTIVE' ||
                                    activeSubscriptionData.plan?.name);

      // Show specific messaging for trial upgrades and downgrades
      if (isTrialUpgrade) {
        toast({
          title: "Upgrading from Trial",
          description: `Upgrading to ${plan.name} plan. Your billing cycle will start for 30 days from today.`,
        });
      } else if (isDowngradeToBasic) {
        toast({
          title: "Downgrading to Basic",
          description: `Switching to ${plan.name} plan.`,
        });
      }

      // Use upgrade endpoint if user has active subscription, otherwise create new checkout session
      let response;
      if (hasActiveSubscription) {
        // This creates an UPGRADE checkout session (for users with active subscriptions)
        const { upgradeSubscription } = await import('../services/subscription-plan-services');
        response = await upgradeSubscription(plan.id);
      } else {
        // This creates a Stripe CHECKOUT session (for payment), not a billing portal session
        // Trial users will complete payment and automatically become paid subscribers
        // Backend will update: account_type -> 'basic'/'full_fledged', account_status -> 'active'
        // Subscription record: current_period_start -> now(), current_period_end -> now() + 30 days
        response = await createCheckoutSession(plan.id);
      }

      if (response?.checkoutUrl) {
        // Ad-pixel "InitiateCheckout" — user is heading to Stripe to pay. This
        // completes the PAID funnel (InitiateCheckout here → Purchase on return
        // success). Fully fire-and-forget: a pixel error can never block the
        // redirect to Stripe. event_id = transactionId so it dedupes with any
        // future server-side Events API call.
        try {
          const value =
            Number((plan as any).price ?? (plan as any).amount ?? (plan as any).monthlyPrice) ||
            undefined;
          const evtId = response?.transactionId || plan.id;
          const params: Record<string, any> = {
            content_type: "subscription",
            content_id: plan.id,
            content_name: plan.name,
          };
          if (value) {
            params.value = value;
            params.currency = "USD";
          }
          const ttq = (window as any).ttq;
          if (ttq && typeof ttq.track === "function") {
            ttq.track("InitiateCheckout", params, { event_id: evtId });
          }
          const fbq = (window as any).fbq;
          if (typeof fbq === "function") {
            fbq("track", "InitiateCheckout", { value, currency: "USD", content_name: plan.name }, { eventID: evtId });
          }
        } catch {
          // never block the Stripe redirect on a pixel failure
        }

        // In development: complete dry run first, then navigate with setTimeout
        if (shouldUseDryRun()) {
          if (response?.transactionId) {
            try {
              await completeDryRunSubscription({
                transactionId: response.transactionId,
              });
            } catch {}
          }

          // In development: use setTimeout with router.push
          setTimeout(() => {
            (allowCheckout("subscription") ? (router.push(response.checkoutUrl)) : undefined);
          }, 100);
        } else {
          // In production: redirect to Stripe checkout
          (allowCheckout("subscription") ? (router.push(response.checkoutUrl)) : undefined);
        }
      }
    } catch (error) {

      toast({
        title: "Error",
        description: "Failed to process subscription. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoadingPlan(null);
    }
  };

  const handleAgencyBillingSave = async () => {
    setIsLoadingAgencySettings(true);
    try {
      const agencyId = user?.currentAgency?.agency?.id;
      if (!agencyId) {
        throw new Error('No agency selected');
      }

      // Get Supabase JWT token for authentication
      const { getSupabaseClient } = await import('@/app/_module/services/supabaseClient');
      const supabase = await getSupabaseClient();
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session?.access_token) {
        throw new Error('No Supabase session found');
      }

      // Call backend API to update agency billing settings
      const response = await fetch(getApiUrl(endpoint.agency.updateBillingSettings(agencyId)), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          seatPayor: agencyBillingSettings.seatPayor,
          plan: agencyBillingSettings.plan,
          billingInterval: agencyBillingSettings.billingInterval,
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to save settings: ${response.statusText}`);
      }

      const result = await response.json();
      toast({
        title: "Settings Updated",
        description: "Agency billing settings have been saved successfully.",
      });
    } catch (error) {

      toast({
        title: "Error",
        description: "Failed to save agency billing settings. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoadingAgencySettings(false);
    }
  };

  // Native app: plan cards are web-only, same instruction-only treatment as
  // the area-code and A2P tabs. After all hooks, before every render branch.
  if (isNativeApp) {
    return (
      <Card className="p-8 text-center">
        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
          <CreditCard className="h-6 w-6 text-primary" />
        </div>
        <h3 className="text-lg font-semibold text-foreground">Account Management</h3>
        <p className="mx-auto mt-2 max-w-sm text-sm text-muted-foreground">
          Plan and account management is available on the web version of
          InsuraCentral. To view or change your plan, log in on your computer
          at app.insuracentral.com.
        </p>
      </Card>
    );
  }

  if (isLoading || isLoadingEnterprise) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading subscription plans...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="text-center py-8">
            <p className="text-red-600 mb-4">Error loading subscription plans</p>
            <p className="text-sm text-gray-500 mb-4">
              {error?.message || 'Unable to fetch plans from server'}
            </p>
            <Button
              onClick={() => queryClient.invalidateQueries({ queryKey: ["subscription-plans"] })}
              variant="outline"
            >
              Retry
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!plans || plans.length === 0) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="text-center py-8">
            <p className="text-gray-600 mb-4">No subscription plans available</p>
            <p className="text-sm text-gray-500">
              Please contact support if this issue persists.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Enterprise OWNERS: show enterprise plan card (their personal subscription is enterprise)
  // Enterprise MEMBERS and non-enterprise users: fall through to regular plan cards
  if (isEnterpriseOwner) {
    return (
      <div className="space-y-6">
        <Card className="border-2 border-purple-200 bg-gradient-to-br from-purple-50 to-white">
          <CardHeader className="text-center pb-4">
            <div className="mx-auto w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mb-3">
              <Crown className="h-8 w-8 text-purple-600" />
            </div>
            <CardTitle className="text-2xl font-bold text-gray-900">
              Enterprise Plan
            </CardTitle>
            <CardDescription className="text-base text-gray-600 mt-1">
              Your enterprise subscription — all features are fully unlocked.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-white rounded-lg border p-4">
                <div className="flex items-center gap-3 mb-2">
                  <Shield className="h-5 w-5 text-green-600" />
                  <h4 className="font-medium text-gray-900">Account Status</h4>
                </div>
                <Badge className="bg-green-100 text-green-800 border-green-200">Active — Enterprise</Badge>
                <p className="text-sm text-gray-500 mt-2">
                  Your enterprise account is active with full access to all features.
                </p>
              </div>
              <div className="bg-white rounded-lg border p-4">
                <div className="flex items-center gap-3 mb-2">
                  <Building className="h-5 w-5 text-blue-600" />
                  <h4 className="font-medium text-gray-900">Plan Type</h4>
                </div>
                <Badge className="bg-purple-100 text-purple-800 border-purple-200">Enterprise</Badge>
                <p className="text-sm text-gray-500 mt-2">
                  Unlimited seats, leads, imports, calls, and all premium features.
                </p>
              </div>
            </div>
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <h4 className="font-medium text-purple-900 mb-2 flex items-center gap-2">
                <Check className="h-4 w-4" /> Enterprise Features Included
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-purple-800">
                <div className="flex items-center gap-2"><Check className="h-3 w-3 text-purple-600" /> Unlimited everything</div>
                <div className="flex items-center gap-2"><Check className="h-3 w-3 text-purple-600" /> White-label branding</div>
                <div className="flex items-center gap-2"><Check className="h-3 w-3 text-purple-600" /> Custom integrations</div>
                <div className="flex items-center gap-2"><Check className="h-3 w-3 text-purple-600" /> Priority support with SLA</div>
                <div className="flex items-center gap-2"><Check className="h-3 w-3 text-purple-600" /> Dedicated account manager</div>
                <div className="flex items-center gap-2"><Check className="h-3 w-3 text-purple-600" /> Custom domain support</div>
                <div className="flex items-center gap-2"><Check className="h-3 w-3 text-purple-600" /> Advanced team management</div>
                <div className="flex items-center gap-2"><Check className="h-3 w-3 text-purple-600" /> Custom AI model training</div>
              </div>
            </div>
            <div className="bg-gray-50 border rounded-lg p-4 text-center">
              <p className="text-sm text-gray-600">
                Manage your agents&apos; subscriptions in the <strong>Agents Billing</strong> tab.
                For billing inquiries, reach out to{" "}
                <a href={`mailto:${branding.supportEmail}`} className="text-blue-600 hover:underline">
                  {branding.supportEmail}
                </a>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Enterprise COVERED MEMBERS: show "covered by org" card instead of "No Plan"
  const isEnterpriseCovered = enterpriseStatus?.isCovered === true && !isEnterpriseOwner;
  if (isEnterpriseCovered) {
    const coveredPlanName = enterpriseStatus?.coveredPlanType === 'full_fledged' || user?.user?.accountType === 'full_fledged' ? 'Full-Fledged' : 'Basic';
    return (
      <div className="space-y-6">
        <Card className="border-2 border-green-200 bg-gradient-to-br from-green-50 to-white">
          <CardHeader className="text-center pb-4">
            <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-3">
              <Shield className="h-8 w-8 text-green-600" />
            </div>
            <CardTitle className="text-2xl font-bold text-gray-900">
              {coveredPlanName} Plan
            </CardTitle>
            <CardDescription className="text-base text-gray-600 mt-1">
              Your subscription is covered by {enterpriseStatus?.enterpriseAgencyName || 'your organization'}.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-white rounded-lg border p-4">
                <div className="flex items-center gap-3 mb-2">
                  <Shield className="h-5 w-5 text-green-600" />
                  <h4 className="font-medium text-gray-900">Account Status</h4>
                </div>
                <Badge className="bg-green-100 text-green-800 border-green-200">Active — Covered</Badge>
                <p className="text-sm text-gray-500 mt-2">
                  Your {coveredPlanName} plan is fully active with all features included.
                </p>
              </div>
              <div className="bg-white rounded-lg border p-4">
                <div className="flex items-center gap-3 mb-2">
                  <Building className="h-5 w-5 text-purple-600" />
                  <h4 className="font-medium text-gray-900">Managed By</h4>
                </div>
                <Badge className="bg-purple-100 text-purple-800 border-purple-200">
                  {enterpriseStatus?.enterpriseAgencyName || 'Enterprise Organization'}
                </Badge>
                <p className="text-sm text-gray-500 mt-2">
                  Your subscription is managed and paid for by your organization.
                </p>
              </div>
            </div>
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
              <p className="text-sm text-green-800">
                No action needed — your subscription is handled by your organization administrator.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show the correct trial plan name based on trialType (basic or full_fledged)
  const getTrialPlanName = () => {
    // Get trial type from multiple sources (fallback chain)
    const getTrialType = (): 'lite' | 'basic' | 'full_fledged' => {
      if (trialType === 'full_fledged') return 'full_fledged';
      const u: any = (user as any)?.user || {};
      // trialAccountTypeUsed is what approval stamps; selectedPlan is what
      // signup stored, and is all there is before approval.
      const userTrialType = String(u.trialAccountTypeUsed || u.trial_account_type_used || u.trialType || u.selectedPlan || u.selected_plan || '').toLowerCase();
      if (userTrialType === 'full_fledged') return 'full_fledged';
      if (userTrialType === 'lite') return 'lite';
      return 'basic';
    };
    const TRIAL_LABEL = { lite: 'Lite Trial', basic: 'Basic Trial', full_fledged: 'Full-Fledged Trial' } as const;
    const actualTrialType = getTrialType();
    const label = TRIAL_LABEL[actualTrialType];
    
    // Check if trial is expired
    if (isExpired && !hasPaidSubscription) return `${label} (Expired)`;
    
    // Active trial
    if (isTrialActive) return label;

    // Signed up for a trial that hasn't started yet (starts at approval, after
    // email verification): say which one, not "No Plan".
    const u: any = (user as any)?.user || {};
    if (!hasPaidSubscription && String(u.accountType || '').toLowerCase() === 'trial' && (u.selectedPlan || u.trialAccountTypeUsed)) {
      return `${label} (Pending approval)`;
    }
    
    return "No Plan";
  };
  const currentPlanName = activeSubscriptionData?.plan?.name || getTrialPlanName();
  const currentStatus = activeSubscriptionData?.status || (isTrialActive ? "trial_active" : "N/A");

  // Get the correct icon for the plan
  const getPlanIcon = (planName: string) => {
    const planLower = planName.toLowerCase();

    if (planLower.includes('premium') || planLower.includes('full_fledged') || planLower.includes('full-fledged')) {
      return <Crown className="h-5 w-5 text-yellow-500" />;
    } else if (planLower.includes('professional')) {
      return <Zap className="h-5 w-5 text-blue-500" />;
    } else if (planLower.includes('starter') || planLower.includes('basic')) {
      return <BarChart3 className="h-5 w-5 text-gray-500" />;
    } else if (planLower.includes('super')) {
      return <InsuraBotIcon className="h-5 w-5 text-amber-500" />;
    } else if (planLower.includes('enterprise')) {
      return <Shield className="h-5 w-5 text-purple-500" />;
    } else if (planLower.includes('lite')) {
      return <Feather className="h-5 w-5 text-slate-500" />;
    }

    // Default to CreditCard for unknown plans
    return <CreditCard className="h-5 w-5 text-gray-500" />;
  };

  // Smart price formatting - shows whole dollars without decimals
  const formatPrice = (price: number) => {
    return price % 1 === 0 ? price.toFixed(0) : price.toFixed(2);
  };

  // Usage Stats Inline Component (rendered inside Current Plan card)
  const UsageStatsInline = () => {
    if (isLoadingUsageStats) {
      return (
        <div className="flex items-center justify-center py-4">
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
          <span className="ml-2 text-sm text-gray-500">Loading usage...</span>
        </div>
      );
    }

    if (!usageStats) return null;

    // Check if user is a trial user - but prioritize hasPaidSubscription and activeSubscriptionData
    // If user has a paid subscription, they are NOT a trial user regardless of usageStats
    const isTrialUserFromStats = usageStats.accountType === 'trial';
    const hasActivePaidSubscription = hasPaidSubscription || !!activeSubscriptionData?.plan;
    const isTrialUser = isTrialUserFromStats && !hasActivePaidSubscription;
    
    const leadsActive = usageStats.leads?.active ?? usageStats.leads?.used ?? 0;
    const leadsUsed = usageStats.leads?.used || 0;
    const leadsLimit = usageStats.leads?.limit;
    const leadsPercentage = leadsLimit === 'Unlimited'
      ? 0
      : Math.min(100, (leadsUsed / (leadsLimit as number)) * 100);

    const dialTimeUsed = usageStats.dialTime?.usedHours || 0;
    const dialTimeLimit = usageStats.dialTime?.limitHours;
    const dialTimePercentage = dialTimeLimit === 'Unlimited'
      ? 0
      : Math.min(100, (dialTimeUsed / (dialTimeLimit as number)) * 100);

    const billingPeriodEnd = usageStats.billingPeriod?.end
      ? new Date(usageStats.billingPeriod.end)
      : null;
    const daysUntilReset = billingPeriodEnd
      ? Math.ceil((billingPeriodEnd.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))
      : 0;

    // For trial users without paid subscription, use the trial days remaining from useTrialStatus hook
    // For paid subscribers, use billing period reset
    const displayDays = ((isTrialUser || isTrialActive) && !hasPaidSubscription) 
      ? (trialDaysRemaining || daysUntilReset) 
      : daysUntilReset;
    
    // Show "Trial ended" if expired and no paid subscription, otherwise show days remaining
    const displayText = ((isTrialUser || isTrialActive) && !hasPaidSubscription)
      ? (isExpired ? 'Trial ended' : `Trial ends in ${displayDays} days`)
      : `Resets in ${displayDays} days`;

    return (
      <div className="space-y-4 pt-4 border-t mt-4 bg-gradient-to-r from-blue-50/50 to-transparent rounded-lg p-4 -mx-2">
        {/* Header with reset info */}
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-gray-700 flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-blue-600" />
            Usage Statistics
          </span>
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <Calendar className="h-3 w-3" />
            <span>{displayText}</span>
          </div>
        </div>

        {/* Lead Usage - Shows total leads created (including deleted) to prevent gaming */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-gray-500" />
              <span className="text-sm font-medium">
                Lead Imports ({(isTrialUser || isTrialActive || isExpired) && !hasPaidSubscription ? '2 weeks' : 'Monthly'})
              </span>
            </div>
            <span className="text-sm text-gray-600">
              {leadsUsed.toLocaleString()} / {leadsLimit === 'Unlimited' ? 'Unlimited' : (leadsLimit as number).toLocaleString()}
            </span>
          </div>
          {leadsLimit !== 'Unlimited' && (
            <Progress value={leadsPercentage} className="h-2" />
          )}
          <div className="flex items-center justify-between text-xs text-gray-500">
            <span>Daily: {isTrialUser ? (usageStats.leads?.dailyUsed || 0) : `${usageStats.leads?.dailyUsed || 0} / ${usageStats.accountType === 'full_fledged' || usageStats.accountType === 'enterprise' || usageStats.accountType === 'super_agent' ? 'Unlimited' : (usageStats.leads?.dailyLimit || 0).toLocaleString()}`}</span>
            {leadsLimit !== 'Unlimited' && (
              <span className={leadsPercentage > 80 ? 'text-orange-600 font-medium' : ''}>
                {usageStats.leads?.remaining} remaining
              </span>
            )}
          </div>
        </div>

        {/* Dial Time - trial users only, and only on a trial that includes a phone
            (a Lite trial has no dialer, so a 0-hour allowance is noise). */}
        {isTrialUser && dialTimeLimit !== 'Unlimited' &&
          accountTypeHasTelephony((user as any)?.user?.accountType, (user as any)?.user?.trialAccountTypeUsed) && (
          <div className="space-y-2 pt-3 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-gray-500" />
                <span className="text-sm font-medium">Dial Time</span>
              </div>
              <span className="text-sm text-gray-600">
                {typeof dialTimeUsed === 'number' ? dialTimeUsed.toFixed(1) : '0'} / {dialTimeLimit} hours
              </span>
            </div>
            <Progress value={dialTimePercentage} className="h-2" />
            <div className="flex items-center justify-between text-xs text-gray-500">
              <span className={dialTimePercentage > 80 ? 'text-orange-600 font-medium' : ''}>
                {typeof usageStats.dialTime?.remainingHours === 'number'
                  ? usageStats.dialTime.remainingHours.toFixed(1)
                  : usageStats.dialTime?.remainingHours} hours remaining
              </span>
              {dialTimePercentage > 80 && (
                <Badge variant="outline" className="text-orange-600 text-xs">
                  Upgrade for unlimited
                </Badge>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Current Plan Status with Integrated Usage Stats */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <CardTitle className="flex items-center gap-2">
                {getPlanIcon(currentPlanName)}
                Current Plan: {currentPlanName}
              </CardTitle>
              <CardDescription>
                {activeError ? (
                  <span className="text-sm text-muted-foreground">Unable to load active subscription.</span>
                ) : activeSubscriptionData ? (
                  <span className="space-y-2 block">
                    <span className="block">Your current plan details</span>
                    <span className="text-sm text-muted-foreground block space-y-1">
                      <span className="block">
                        Status:{" "}
                        <Badge variant="outline">{humanizeStatus(currentStatus)}</Badge>
                      </span>
                      {!!activeSubscriptionData.startDate && (
                        <span className="block">
                          Started:{" "}
                          {new Date(activeSubscriptionData.startDate).toLocaleDateString()}
                        </span>
                      )}
                      {!!activeSubscriptionData.currentPeriodEnd && (
                        <span className="block">
                          {activeSubscriptionData.autoRenew ? 'Next billing' : 'Ends'}:{" "}
                          {new Date(activeSubscriptionData.currentPeriodEnd).toLocaleDateString()}
                        </span>
                      )}
                      {activeSubscriptionData.autoRenew !== undefined && (
                        <span className="block">
                          Auto-renewal:{" "}
                          {activeSubscriptionData.autoRenew ? (
                            <Badge className="text-green-600" variant="outline">
                              Enabled
                            </Badge>
                          ) : (
                            <Badge className="text-gray-600" variant="outline">
                              Disabled
                            </Badge>
                          )}
                        </span>
                      )}
                      {activeSubscriptionData.areaCodeSubscriptions && activeSubscriptionData.areaCodeSubscriptions.length > 0 && (
                        <span className="mt-2 pt-2 border-t block">
                          <span className="font-medium">Area Codes:</span>{" "}
                          {activeSubscriptionData.areaCodeSubscriptions.length} active
                          ({activeSubscriptionData.areaCodeSubscriptions.filter((ac: any) => ac.isLifetimeFree).length} free,{" "}
                          {activeSubscriptionData.areaCodeSubscriptions.filter((ac: any) => !ac.isLifetimeFree).length} paid)
                        </span>
                      )}
                    </span>
                  </span>
                ) : isTrialActive ? (
                  <TrialCountdown 
                    trialEndDate={trialEndDate} 
                    daysRemaining={trialDaysRemaining}
                    trialType={trialType}
                    hasPaidSubscription={hasPaidSubscription}
                  />
                ) : (
                  isExpired && !hasPaidSubscription 
                    ? "Your trial has expired. Upgrade to continue using all features."
                    : "You don't have an active subscription"
                )}
              </CardDescription>
            </div>
            {activeSubscriptionData && (
              <Button
                variant="outline"
                onClick={async () => {
                  try {
                    const session = await getSupabaseSession();
                    if (!session?.access_token) {
                      toast({
                        title: "Authentication Required",
                        description: "Please sign in to manage your subscription.",
                        variant: "destructive",
                      });
                      return;
                    }

                    // Create Stripe Customer Portal session
                    const response = await fetch(getApiUrl('/stripe/create-portal-session'), {
                      method: 'POST',
                      headers: {
                        'Authorization': `Bearer ${session.access_token}`,
                        'Content-Type': 'application/json',
                      },
                      body: JSON.stringify({
                        returnUrl: `${window.location.origin}/settings?tab=subscription`,
                      }),
                    });

                    if (!response.ok) {
                      throw new Error('Failed to create portal session');
                    }

                    const data = await response.json();

                    // Check if user is agency owner with restrictions
                    if (data.data?.restrictedDowngrade) {
                      // Show modal instead of redirecting to portal
                      setShowAgencyOwnerModal(true);
                      toast({
                        title: "Agency Owner Restrictions",
                        description: "As an agency owner, you cannot downgrade your subscription. Please contact support.",
                        variant: "default",
                      });
                      return;
                    }

                    if (data.data?.url) {
                      // Set flag in sessionStorage before navigating (persists across page reload)
                      sessionStorage.setItem('billingPortalOpened', 'true');
                      window.location.href = data.data.url;
                    } else {
                      throw new Error('No portal URL received');
                    }
                  } catch (error) {

                    toast({
                      title: "Error",
                      description: "Failed to open billing portal. Please try again.",
                      variant: "destructive",
                    });
                  }
                }}
              >
                <CreditCard className="h-4 w-4 mr-2" />
                Manage Subscription
              </Button>
            )}
            {/* Verify reconciles Stripe purchases against the account. A trial has
                no purchases yet, so the button only shows once there's a paid
                subscription — and it dresses like every other header action. */}
            {hasPaidSubscription && (
            <Button
              variant="outline"
              size="sm"
              className="h-9 gap-1.5 border-blue-200 text-blue-700 hover:bg-blue-50"
              onClick={async () => {
              // DEMO SANDBOX: "Verify Purchases" reconciles against the LIVE Stripe
              // account — it re-reads real sessions and can provision or cancel numbers off
              // the back of them. A sandbox visitor must never touch that reconciliation,
              // and there is nothing of theirs to verify anyway.
              if (isDemoMode()) {
                window.dispatchEvent(
                  new CustomEvent('demo-restricted', {
                    detail:
                      "Verifying purchases is disabled in the demo sandbox. Start a free trial to manage real billing.",
                  }),
                );
                return;
              }
                try {
                  const { getSupabaseSession } = await import("@/app/_module/services/auth");
                  const { getApiUrl } = await import("@/app/_module/services/config");
                  const { endpoint } = await import("@/app/_module/services/endpoints");
                  const session = await getSupabaseSession();
                  const res = await fetch(getApiUrl(endpoint.stripe.verifyPurchases), {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session?.access_token}` },
                    body: '{}',
                  });
                  const json = await res.json();
                  const data = json?.data;
                  // Toast for the glance, panel for the detail.
                  setVerifyError(null);
                  setVerifyResult(data ?? null);
                  setVerifyOpen(true);
                  if (data?.provisioned?.length > 0 || data?.subscriptionFix?.fixed || data?.duplicateSubscriptions?.length > 0 || data?.webhookHealth?.reEnabled) {
                    toast({ title: "Issues Found & Fixed", description: data.summary });
                  } else {
                    toast({ title: "All Good", description: data?.summary || "All purchases verified." });
                  }
                } catch {
                  setVerifyResult(null);
                  setVerifyError("We could not reach the billing service. Please try again in a moment.");
                  setVerifyOpen(true);
                  toast({ title: "Verification Failed", description: "Please try again.", variant: "destructive" });
                }
              }}
              title="Verify all purchases match your account"
            >
              <ShieldCheck className="h-4 w-4" />
              Verify purchases
            </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          {/* View Usage Button */}
          <button
            onClick={() => setShowUsageStats(!showUsageStats)}
            className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 font-medium transition-colors w-full justify-center py-2 border rounded-lg hover:bg-blue-50"
          >
            <TrendingUp className="h-4 w-4" />
            {showUsageStats ? 'Hide Usage Statistics' : 'View Usage Statistics'}
            {showUsageStats ? (
              <ChevronUp className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )}
          </button>

          {/* Collapsible Usage Stats Section */}
          {showUsageStats && <UsageStatsInline />}
        </CardContent>
      </Card>

      {/* Trial Upgrade Information */}
      {(activeSubscriptionData?.status === 'trialing' || activeSubscriptionData?.status === 'trial_active') && (
        <Card className="border-blue-200 bg-blue-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-blue-800">
              <Crown className="h-5 w-5" />
              Trial Upgrade Information
            </CardTitle>
            <CardDescription className="text-blue-700">
              <div className="space-y-2">
                <p>✅ You're currently on a trial - you can upgrade to any paid plan</p>
                <p>📅 When you upgrade, your billing cycle will start immediately for 30 days</p>
                <p>💳 You'll be charged based on your selected billing period (monthly/yearly)</p>
                <p>🔄 Your trial features will seamlessly transition to your chosen plan</p>
              </div>
            </CardDescription>
          </CardHeader>
        </Card>
      )}

      {/* Environment Info (for debugging) - DISABLED */}
      {/* {process.env.NODE_ENV !== "production" && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm text-muted-foreground">
              Environment: {process.env.NODE_ENV} | Dry Run:{" "}
              {shouldUseDryRun() ? "Enabled" : "Disabled"}
            </CardTitle>
          </CardHeader>
        </Card>
      )} */}

      {/* Billing Toggle */}
      <Card>
        <CardContent className="p-6">
          {/* Billing toggle - YEARLY DISABLED */}
          <div className="flex items-center justify-center gap-4">
            <span className="font-medium">
              Monthly Billing
            </span>
            {/* Yearly option temporarily disabled */}
            {/* <Switch
              checked={billingPeriod === "yearly"}
              onCheckedChange={(checked) =>
                setBillingPeriod(checked ? "yearly" : "monthly")
              }
            />
            <span
              className={
                billingPeriod === "yearly"
                  ? "font-medium"
                  : "text-muted-foreground"
              }
            >
              Yearly
            </span>
            {billingPeriod === "yearly" && (
              <Badge className="bg-green-100 text-green-800">Save 20%</Badge>
            )} */}
          </div>
        </CardContent>
      </Card>

      {/* Error State */}
      {error && (
        <Card>
          <CardContent className="text-center py-8">
            <p className="text-red-600">Error loading subscription plans: {(error as any)?.message || 'Unknown error'}</p>
            <Button
              onClick={() => queryClient.invalidateQueries({ queryKey: ["subscription-plans"] })}
              className="mt-4"
              variant="outline"
            >
              Retry
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Loading State */}
      {isLoading && (
        <Card>
          <CardContent className="text-center py-8">
            <p className="text-muted-foreground">Loading subscription plans...</p>
          </CardContent>
        </Card>
      )}

      {/* Plans Grid */}
      {!isLoading && !error && plans.length === 0 ? (
        <Card>
          <CardContent className="text-center py-8">
            <p className="text-muted-foreground">No subscription plans available. Please contact support.</p>
          </CardContent>
        </Card>
      ) : !isLoading && !error ? (
        <>
        {(() => {
          const visiblePlans = sortedPlans.filter(p => !p.name.toLowerCase().includes('enterprise'));
          const enterprisePlan = sortedPlans.find(p => p.name.toLowerCase().includes('enterprise'));
          return (
            <>
            <div className="grid gap-6" style={{ gridTemplateColumns: `repeat(${visiblePlans.length}, minmax(280px, 1fr))` }}>
          {visiblePlans.map((plan) => {
            const theme = getPlanTheme(plan.name);
            const nameLower = plan.name.toLowerCase();
            const isSuper = nameLower.includes("super");
            const allLabels = (plan.features || []).map((f) => formatFeature(String(f)));
            const includedFeatures = allLabels.filter((l) => !isExclusionFeature(l));
            const excludedFeatures = allLabels.filter((l) => isExclusionFeature(l));
            return (
          <Card
            key={plan.name}
            className={`relative flex flex-col rounded-2xl bg-white transition-all duration-300 hover:-translate-y-1 ${
              plan.current
                ? "border-transparent ring-2 ring-emerald-500 shadow-[0_24px_60px_-28px_rgba(16,185,129,0.45)]"
                : plan.isPopular && !isSuper
                  ? "border-transparent ring-2 ring-indigo-500 shadow-[0_24px_60px_-24px_rgba(79,70,229,0.45)]"
                  : isSuper
                    ? "border border-amber-200 shadow-[0_24px_60px_-28px_rgba(217,119,6,0.35)]"
                    : "border border-slate-200 hover:border-slate-300 hover:shadow-[0_24px_60px_-28px_rgba(15,23,42,0.3)]"
            }`}
          >
            {/* Tier keyline — ONLY on cards without a ring. A ringed card (current /
                most-popular) already gets its color from the ring itself; drawing the
                keyline too produced two parallel lines with a gap at the top edge. */}
            {!plan.current && !(plan.isPopular && !isSuper) && (
              <div className={`absolute inset-x-0 top-0 h-1 rounded-t-2xl bg-gradient-to-r ${theme.bar}`} />
            )}

            {isComingSoonPlan(plan.name) && !plan.current && (
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10">
                <Badge className="rounded-full bg-amber-500 px-3.5 py-1 text-[11px] font-semibold uppercase tracking-wider text-white shadow-sm hover:bg-amber-500">
                  Coming Soon
                </Badge>
              </div>
            )}
            {!!plan.isPopular && !isSuper && !plan.current && (
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10">
                <Badge className="rounded-full bg-indigo-600 px-3.5 py-1 text-[11px] font-semibold uppercase tracking-wider text-white shadow-sm hover:bg-indigo-600">
                  Most Popular
                </Badge>
              </div>
            )}
            {!!plan.current && !isSuper && (
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10">
                <Badge className="rounded-full bg-emerald-600 px-3.5 py-1 text-[11px] font-semibold uppercase tracking-wider text-white shadow-sm hover:bg-emerald-600">
                  Current Plan
                </Badge>
              </div>
            )}

            <CardHeader className="pb-4 pt-7">
              <div className={`mb-4 inline-flex h-10 w-10 items-center justify-center rounded-xl ${theme.iconWrap}`}>
                {isSuper ? (
                  <InsuraBotIcon className="h-5 w-5" />
                ) : nameLower.includes("premium") || nameLower.includes("full_fledged") || nameLower.includes("full-fledged") ? (
                  <Crown className="h-5 w-5" />
                ) : nameLower.includes("professional") ? (
                  <Zap className="h-5 w-5" />
                ) : nameLower.includes("lite") ? (
                  <Feather className="h-5 w-5" />
                ) : (
                  <BarChart3 className="h-5 w-5" />
                )}
              </div>
              <CardTitle className="text-lg font-semibold tracking-tight text-slate-900">
                {plan.name}
              </CardTitle>
              <p className="mt-0.5 text-[13px] leading-snug text-muted-foreground">{theme.tagline}</p>
              <div className="mt-4 flex items-baseline gap-1.5">
                {isSuper ? (
                  <span className="text-3xl font-bold tracking-tight text-amber-600">Coming Soon</span>
                ) : (
                  <>
                    <span className={`text-[40px] font-bold leading-none tracking-tight tabular-nums ${theme.price}`}>
                      <span className="mr-0.5 align-top text-[22px] font-semibold">$</span>
                      {formatPrice(plan.price)}
                    </span>
                    <span className="text-sm font-medium text-muted-foreground">/ month</span>
                  </>
                )}
              </div>
            </CardHeader>

            <CardContent className="flex flex-1 flex-col pt-0">
              {isSuper ? (
                <div className="relative min-h-[280px] flex-1 border-t border-slate-100 pt-4">
                  <ul className="select-none space-y-2.5 blur-[7px] pointer-events-none" aria-hidden="true">
                    {[
                      "Lorem ipsum dolor sit amet consectetur",
                      "Vestibulum ante primis in faucibus",
                      "Praesent commodo cursus magna vel",
                      "Cras mattis consectetur purus sit amet",
                      "Donec sed odio dui nulla vitae elit",
                      "Maecenas faucibus mollis interdum",
                      "Etiam porta sem malesuada magna",
                      "Nullam quis risus eget urna mollis",
                    ].map((text, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <Check className={`mt-0.5 h-4 w-4 flex-shrink-0 ${theme.check}`} />
                        <span className="text-[13px] leading-snug text-slate-600">{text}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="absolute inset-0 -mt-6 flex flex-col items-center justify-center px-4 text-center">
                    <span className="mb-3 text-4xl">🤫</span>
                    <p className="text-sm text-muted-foreground">Something big is coming to insurance agents...</p>
                  </div>
                </div>
              ) : (
                <div className="flex-1 border-t border-slate-100 pt-4">
                  <p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-400">
                    What&apos;s included
                  </p>
                  <ul className="space-y-2.5">
                    {includedFeatures.map((label, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <Check className={`mt-0.5 h-4 w-4 flex-shrink-0 ${theme.check}`} />
                        <span className="text-[13px] leading-snug text-slate-600">{label}</span>
                      </li>
                    ))}
                  </ul>
                  {excludedFeatures.length > 0 && (
                    <ul className="mt-3 space-y-2.5 border-t border-dashed border-slate-200 pt-3">
                      {excludedFeatures.map((label, index) => (
                        <li key={index} className="flex items-start gap-2">
                          <X className="mt-0.5 h-4 w-4 flex-shrink-0 text-slate-300" />
                          <span className="text-[13px] leading-snug text-slate-400">{label}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}

              <Button
                className={`mt-6 w-full font-semibold ${
                  isComingSoonPlan(plan.name) && !plan.current
                    ? "cursor-not-allowed border border-amber-200 bg-amber-50 text-amber-600 shadow-none hover:bg-amber-50"
                    : plan.isPopular && !plan.current
                      ? "bg-indigo-600 text-white shadow-lg shadow-indigo-600/25 hover:bg-indigo-700"
                      : ""
                }`}
                size="lg"
                disabled={plan.current || loadingPlan === plan.id || isComingSoonPlan(plan.name)}
                onClick={() =>
                  plan.current || isComingSoonPlan(plan.name)
                    ? undefined
                    : handleSubscribe(plan as Plan & { id: string })
                }
                variant={
                  plan.current
                    ? "secondary"
                    : isComingSoonPlan(plan.name)
                      ? "default"
                      : plan.isPopular
                        ? "default"
                        : "outline"
                }
              >
                {loadingPlan === plan.id
                  ? "Processing..."
                  : plan.current
                    ? "Current Plan"
                    : isComingSoonPlan(plan.name)
                      ? "Coming Soon"
                      : (activeSubscriptionData?.status === 'trialing' || activeSubscriptionData?.status === 'trial_active' ||
                         // A trial started at approval has no subscription row at all —
                         // the user record is the only place it shows.
                         (isTrialActive && !activeSubscriptionData?.plan?.name))
                          ? (() => {
                              // On a trial: the plan being trialed is the one to
                              // subscribe to; a lower tier is a switch, not an upgrade.
                              const u: any = (user as any)?.user || user || {};
                              const trialOf = u.trialAccountTypeUsed || u.selectedPlan || trialType;
                              const trialRank = planRank(trialOf);
                              const target = planRank(plan.name);
                              if (trialRank && target === trialRank) return `Subscribe to ${plan.name}`;
                              if (trialRank && target < trialRank) return `Switch Trial to ${plan.name}`;
                              return `Upgrade Trial to ${plan.name}`;
                            })()
                          : (() => {
                              // Paid: word it by tier. "Upgrade to Lite" from
                              // Full-Fledged read backwards.
                              const u: any = (user as any)?.user || user || {};
                              const currentRank = planRank(
                                activeSubscriptionData?.plan?.name || u.accountType || u.selectedPlan,
                              );
                              const target = planRank(plan.name);
                              return currentRank && target && target < currentRank
                                ? `Downgrade to ${plan.name}`
                                : `Upgrade to ${plan.name}`;
                            })()}
              </Button>
            </CardContent>
          </Card>
            );
          })}
            </div>

            {/* Enterprise — accordion: the panel header is always visible and IS the
                toggle. Clicking anywhere on it expands/collapses the feature grid; the
                Contact Sales button stops propagation so it never toggles the panel. */}
            {enterprisePlan && (
              <Card className="relative mt-8 overflow-hidden rounded-2xl border-0 bg-slate-900 text-white shadow-none">
                {/* Keyline + ambient glow — the one dark, premium moment on the page */}
                <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-violet-500 via-purple-400 to-fuchsia-400" />
                <div className="pointer-events-none absolute -top-24 right-[-4rem] h-72 w-72 rounded-full bg-violet-500/20 blur-3xl" />
                <div className="pointer-events-none absolute -bottom-32 left-[-6rem] h-80 w-80 rounded-full bg-fuchsia-500/10 blur-3xl" />

                <CardHeader
                  role="button"
                  tabIndex={0}
                  aria-expanded={showEnterprise}
                  onClick={() => setShowEnterprise(!showEnterprise)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setShowEnterprise(!showEnterprise);
                    }
                  }}
                  className="relative cursor-pointer select-none pb-6 pt-8 transition-colors hover:bg-white/[0.03]"
                >
                  <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
                    <div className="flex items-center gap-4">
                      <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-violet-500/15 text-violet-300 ring-1 ring-violet-400/30">
                        <Shield className="h-5 w-5" />
                      </div>
                      <div>
                        <CardTitle className="text-xl font-semibold tracking-tight text-white">
                          {enterprisePlan.name}
                        </CardTitle>
                        <p className="mt-0.5 text-[13px] text-slate-400">
                          Custom pricing &middot; dedicated success team &middot; everything unlocked
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <Button
                        className="bg-white px-8 font-semibold text-slate-900 shadow-none hover:bg-slate-100"
                        size="lg"
                        disabled={enterprisePlan.current}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!enterprisePlan.current) handleSubscribe(enterprisePlan as Plan & { id: string });
                        }}
                      >
                        {enterprisePlan.current ? "Current Plan" : "Contact Sales"}
                      </Button>
                      <ChevronDown
                        className={`h-5 w-5 text-slate-400 transition-transform duration-300 ${showEnterprise ? "rotate-180" : ""}`}
                      />
                    </div>
                  </div>
                </CardHeader>

                {/* grid-rows 0fr→1fr height animation — no animate-* classes, which are
                    dead under Tailwind v4 in this repo (config file ignored). */}
                <div
                  className={`grid transition-all duration-500 ease-in-out ${
                    showEnterprise ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
                  }`}
                >
                  <div className="overflow-hidden">
                    <CardContent className="relative pb-8 pt-0">
                      <div className="border-t border-white/10 pt-6">
                        <ul className="grid grid-cols-1 gap-x-8 gap-y-2.5 md:grid-cols-2">
                          {(enterprisePlan.features || []).map((feature, index) => (
                            <li key={index} className="flex items-start gap-2">
                              <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-violet-300" />
                              <span className="text-[13px] leading-snug text-slate-300">{formatFeature(String(feature))}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </CardContent>
                  </div>
                </div>
              </Card>
            )}
            </>
          );
        })()}
        </>
      ) : null}

      {/* Agency Payment Plan - Separate UI for Agency Owners */}
      {isAgencyOwner && agencyPaymentPlan && (() => {
        const plan = agencyPaymentPlan as Plan; // Type guard
        return (
        <div className="mt-8">
          <div className="border-t pt-6 mb-6">
            <div className="flex items-center gap-2 mb-4">
              <Building className="h-5 w-5 text-purple-600" />
              <h3 className="text-lg font-semibold">Agency Payment Option</h3>
            </div>
            <p className="text-sm text-gray-600 mb-6">
              As an agency owner, you can choose to pay for your agents' subscriptions centrally
            </p>
          </div>

          <Card className="border-purple-200 bg-gradient-to-br from-purple-50 to-blue-50">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <Users className="h-6 w-6 text-purple-600" />
                  </div>
                  <div>
                    <CardTitle className="text-xl">{plan.name}</CardTitle>
                    <CardDescription className="mt-1">
                      Centralized billing for all your agents
                    </CardDescription>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-3xl font-bold text-purple-600">
                    {plan.price > 0 ? (
                      <>
                        ${formatPrice(plan.price)}
                        <span className="text-sm font-normal text-gray-600">/{plan.period}</span>
                      </>
                    ) : (
                      <span className="text-lg">Variable Pricing</span>
                    )}
                  </div>
                  <p className="text-sm text-gray-600 mt-1">Per agent seat</p>
                </div>
              </div>
            </CardHeader>

            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-3">
                  <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                    <Check className="h-4 w-4 text-green-500" />
                    Benefits
                  </h4>
                  <ul className="space-y-2">
                    {plan.features.map((feature, index) => {
                      const label = formatFeature(String(feature));
                      const excluded = isExclusionFeature(label);
                      return (
                        <li key={index} className="flex items-start gap-2">
                          {excluded ? (
                            <X className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                          ) : (
                            <Check className="h-4 w-4 text-purple-500 mt-0.5 flex-shrink-0" />
                          )}
                          <span className={`text-sm ${excluded ? "text-muted-foreground" : "text-gray-700"}`}>{label}</span>
                        </li>
                      );
                    })}
                  </ul>
                </div>

                <div className="space-y-3">
                  <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                    <DollarSign className="h-4 w-4 text-green-500" />
                    How it works
                  </h4>
                  <ul className="space-y-2 text-sm text-gray-700">
                    <li className="flex items-start gap-2">
                      <span className="text-purple-600 font-medium">1.</span>
                      <span>Select a subscription plan for your agents (Basic or Full-Fledged)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-purple-600 font-medium">2.</span>
                      <span>Pay for all agent seats from your agency account</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-purple-600 font-medium">3.</span>
                      <span>Agents get instant access without individual billing</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-purple-600 font-medium">4.</span>
                      <span>Manage all billing centrally from your agency dashboard</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="pt-4 border-t">
                <Button
                  className="w-full md:w-auto bg-purple-600 hover:bg-purple-700"
                  disabled={plan.current || loadingPlan === plan.id}
                  onClick={() => {
                    toast({
                      title: "Configure Agency Billing",
                      description: "Use the Agency Billing Settings section below to configure this option.",
                    });
                    // Scroll to agency billing settings section
                    const agencyBillingSection = document.getElementById('agency-billing-settings');
                    if (agencyBillingSection) {
                      agencyBillingSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                  }}
                >
                  {plan.current ? "Current Plan" : "Configure Agency Billing"}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
        );
      })()}

      {/* Agency Billing Section - Only for Agency Owners */}
      {isAgencyOwner && (
        <div className="space-y-6" id="agency-billing-settings">
          <div className="border-t pt-6">
            <div className="flex items-center gap-2 mb-4">
              <Shield className="h-5 w-5 text-blue-600" />
              <h3 className="text-lg font-semibold">Agency Billing Settings</h3>
            </div>
            
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building className="h-5 w-5" />
                  Billing Configuration
                </CardTitle>
                <CardDescription>
                  Configure how your agency handles billing for team members
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Seat Payor Setting */}
                <div className="space-y-3">
                  <Label className="text-sm font-medium">Who pays for seats?</Label>
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="seat-payor"
                      checked={agencyBillingSettings.seatPayor === 'agency'}
                      onCheckedChange={(checked) =>
                        setAgencyBillingSettings(prev => ({
                          ...prev,
                          seatPayor: checked ? 'agency' : 'agent'
                        }))
                      }
                    />
                    <Label htmlFor="seat-payor" className="text-sm">
                      {agencyBillingSettings.seatPayor === 'agency' 
                        ? 'Agency pays for all seats' 
                        : 'Agents pay for their own seats'}
                    </Label>
                  </div>
                </div>

                {/* Plan Selection */}
                <div className="space-y-3">
                  <Label className="text-sm font-medium">Default Plan</Label>
                  <div className="grid grid-cols-2 gap-3">
                    <Button
                      variant={agencyBillingSettings.plan === 'basic' ? 'default' : 'outline'}
                      onClick={() => setAgencyBillingSettings(prev => ({ ...prev, plan: 'basic' }))}
                      className="justify-start"
                    >
                      <Zap className="h-4 w-4 mr-2" />
                      Basic
                    </Button>
                    <Button
                      variant={agencyBillingSettings.plan === 'full_fledged' ? 'default' : 'outline'}
                      onClick={() => setAgencyBillingSettings(prev => ({ ...prev, plan: 'full_fledged' }))}
                      className="justify-start"
                    >
                      <Crown className="h-4 w-4 mr-2" />
                      Full Fledged
                    </Button>
                  </div>
                </div>

                {/* Billing Interval */}
                <div className="space-y-3">
                  <Label className="text-sm font-medium">Billing Interval</Label>
                  {/* Billing interval - YEARLY DISABLED */}
                  <div className="flex items-center space-x-2">
                    <Label className="text-sm font-medium">
                      Monthly billing
                    </Label>
                    {/* Yearly option temporarily disabled */}
                    {/* <Switch
                      id="billing-interval"
                      checked={agencyBillingSettings.billingInterval === 'yearly'}
                      onCheckedChange={(checked) =>
                        setAgencyBillingSettings(prev => ({
                          ...prev,
                          billingInterval: checked ? 'yearly' : 'monthly'
                        }))
                      }
                    />
                    <Label htmlFor="billing-interval" className="text-sm">
                      {agencyBillingSettings.billingInterval === 'yearly'
                        ? 'Yearly billing (20% discount)'
                        : 'Monthly billing'}
                    </Label> */}
                  </div>
                </div>

                {/* Agency Info */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <Users className="h-4 w-4 text-gray-600" />
                    <span className="text-sm font-medium">Current Agency</span>
                  </div>
                  <p className="text-sm text-gray-600">
                    {user?.currentAgency?.agency?.name || 'No agency selected'}
                  </p>
                </div>

                {/* Save Button */}
                <Button
                  onClick={handleAgencyBillingSave}
                  disabled={isLoadingAgencySettings}
                  className="w-full"
                >
                  {isLoadingAgencySettings ? 'Saving...' : 'Save Agency Billing Settings'}
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Agent Seat Management Dashboard - Always visible */}
          <div className="mt-8">
            <div className="mb-4">
              <h3 className="text-xl font-semibold">Agent Seat Management</h3>
              <p className="text-gray-600 mt-1">
                Manage individual agent subscriptions and billing
              </p>
            </div>
            <AgencyBillingDashboard />
          </div>
        </div>
      )}

      {/* Lite downgrade: numbers go away — confirm before checkout */}
      <Dialog open={!!liteDowngradePlan} onOpenChange={(open) => { if (!open) setLiteDowngradePlan(null); }}>
        <ResponsiveDialogContent size="lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-orange-600">
              <AlertTriangle className="h-5 w-5" />
              Switch to Lite?
            </DialogTitle>
            <DialogDescription className="pt-4 space-y-4">
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 space-y-2">
                <p className="text-sm text-gray-700">
                  Lite is the CRM without the dialers and calling. When the switch goes through:
                </p>
                <ul className="text-sm text-gray-700 list-disc pl-5 space-y-1">
                  <li>Every phone number on this account is deactivated and its monthly charge stops.</li>
                  <li>Numbers are held for 7 days, then released. Upgrade back within that window to keep them.</li>
                  <li>Calling, texting, faxing and the power dialer turn off. Leads, notes, policies and history stay.</li>
                </ul>
              </div>
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setLiteDowngradePlan(null)}>Keep my current plan</Button>
            <Button
              onClick={() => {
                const plan = liteDowngradePlan;
                setLiteDowngradePlan(null);
                if (plan) void handleSubscribe(plan, true);
              }}
            >
              Switch to Lite and deactivate my numbers
            </Button>
          </div>
        </ResponsiveDialogContent>
      </Dialog>

      {/* Agency Owner Downgrade Prevention Modal */}
      <Dialog open={showAgencyOwnerModal} onOpenChange={setShowAgencyOwnerModal}>
        <ResponsiveDialogContent size="lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-orange-600">
              <AlertTriangle className="h-5 w-5" />
              Agency Owner Restriction
            </DialogTitle>
            <DialogDescription className="pt-4 space-y-4">
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                <p className="text-sm text-gray-700 mb-3">
                  As an agency owner, you cannot downgrade your subscription to Basic.
                </p>
                <p className="text-sm text-gray-700 mb-3">
                  Your agency members depend on your Full-Fledged account features.
                </p>
                <p className="text-sm text-gray-700">
                  If you need to make changes to your subscription, please contact our support team for assistance.
                </p>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm font-medium text-gray-900 mb-2">
                  Contact Support:
                </p>
                <a
                  href={`mailto:${branding.supportEmail}`}
                  className="text-sm text-blue-600 hover:text-blue-800 underline"
                >
                  {branding.supportEmail}
                </a>
              </div>

              <div className="text-xs text-gray-500 pt-2">
                <p className="font-medium mb-1">Note:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>You can still upgrade to higher plans</li>
                  <li>You can update your payment methods</li>
                  <li>You can cancel your subscription (at period end)</li>
                </ul>
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              onClick={() => setShowAgencyOwnerModal(false)}
              variant="default"
            >
              I Understand
            </Button>
          </DialogFooter>
        </ResponsiveDialogContent>
      </Dialog>

      {/* Calendly Widget - Bottom Right Popup */}
      {showCalendlyPopup && (
        <div className="fixed bottom-4 right-4 z-50 animate-in slide-in-from-bottom-5 slide-in-from-right-5 duration-300">
          <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden w-[380px] h-[600px] flex flex-col">
            {/* Header */}
            <div className="bg-gradient-to-r from-purple-600 to-purple-700 px-4 py-3 flex items-center justify-between">
              <div className="flex items-center gap-2 text-white">
                <Shield className="h-5 w-5" />
                <span className="font-semibold">Schedule a Call</span>
              </div>
              <button
                onClick={() => setShowCalendlyPopup(false)}
                className="text-white/80 hover:text-white hover:bg-white/10 rounded-full p-1 transition-colors"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
            {/* Calendly iframe */}
            <div className="flex-1 bg-white">
              <iframe
                src={ENTERPRISE_CALENDLY_URL}
                width="100%"
                height="100%"
                frameBorder="0"
                title={`Schedule a meeting with ${branding.brandName} Sales`}
                className="w-full h-full"
              />
            </div>
          </div>
        </div>
      )}

      <VerifyPurchasesResult
        open={verifyOpen}
        onOpenChange={setVerifyOpen}
        data={verifyResult}
        error={verifyError}
      />
    </div>
  );
};
